import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, FileText, User, Cpu, Terminal, Code } from "lucide-react";
import { cn } from "@/lib/utils";
import { SeverityToggle } from "@/components/admin/SeverityToggle";
import { ErrorTabContent } from "@/components/admin/ErrorTabContent";
import { useErrorConsole, DATE_RANGES } from "@/hooks/use-error-console";
import { ERROR_TABS } from "@/lib/error-types";
import type { ErrorSource } from "@/lib/error-types";

// Icon map for tabs
const SOURCE_ICONS: Record<ErrorSource, React.ReactNode> = {
  compiler: <FileText className="h-4 w-4" />,
  auth: <User className="h-4 w-4" />,
  llm: <Cpu className="h-4 w-4" />,
  function: <Terminal className="h-4 w-4" />,
  extraction: <Code className="h-4 w-4" />,
};

export default function Errors() {
  const {
    activeTab,
    setActiveTab,
    dateRange,
    setDateRange,
    severityFilter,
    setSeverityFilter,
    selectedErrorCode,
    setSelectedErrorCode,
    compilerHook,
    authHook,
    llmHook,
    functionHook,
    extractionHook,
    refresh,
    isRefreshing,
    getFilters,
  } = useErrorConsole();

  // Get the current hook based on active tab
  const getCurrentHookData = () => {
    switch (activeTab) {
      case "compiler":
        return compilerHook;
      case "auth":
        return authHook;
      case "llm":
        return llmHook;
      case "function":
        return functionHook;
      case "extraction":
        return extractionHook;
      default:
        return compilerHook;
    }
  };

  const currentHook = getCurrentHookData();
  const isLoading =
    currentHook.loading ||
    currentHook.summaryLoading ||
    currentHook.distributionLoading;

  // Handle page change for current tab
  const handlePageChange = (page: number) => {
    const filters = getFilters();
    currentHook.fetchErrors(filters, page);
  };

  return (
    <AdminLayout
      title="Error Console"
      description="Monitor and analyze errors across all platform sources"
    >
      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Date Range Selector */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[130px] sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Severity Toggle */}
          <SeverityToggle
            value={severityFilter}
            onChange={setSeverityFilter}
            disabled={isLoading}
          />
        </div>

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={isRefreshing}
        >
          <RefreshCw
            className={cn(
              "h-4 w-4 sm:mr-2",
              isRefreshing && "animate-spin"
            )}
          />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ErrorSource)}
        className="space-y-6 overflow-hidden"
      >
        <TabsList className="grid grid-cols-5 w-full">
          {ERROR_TABS.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="flex items-center gap-1 sm:gap-2 px-1 sm:px-3 text-xs sm:text-sm"
            >
              {SOURCE_ICONS[tab.id]}
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Compiler Tab */}
        <TabsContent value="compiler" className="mt-6">
          <ErrorTabContent
            source="compiler"
            errors={compilerHook.errors}
            total={compilerHook.total}
            page={compilerHook.page}
            pageSize={compilerHook.pageSize}
            loading={compilerHook.loading}
            summary={compilerHook.summary}
            distribution={compilerHook.distribution}
            summaryLoading={compilerHook.summaryLoading}
            distributionLoading={compilerHook.distributionLoading}
            selectedErrorCode={selectedErrorCode}
            onSelectErrorCode={setSelectedErrorCode}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        {/* Auth Tab */}
        <TabsContent value="auth" className="mt-6">
          <ErrorTabContent
            source="auth"
            errors={authHook.errors}
            total={authHook.total}
            page={authHook.page}
            pageSize={authHook.pageSize}
            loading={authHook.loading}
            summary={authHook.summary}
            distribution={authHook.distribution}
            summaryLoading={authHook.summaryLoading}
            distributionLoading={authHook.distributionLoading}
            selectedErrorCode={selectedErrorCode}
            onSelectErrorCode={setSelectedErrorCode}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        {/* LLM Tab */}
        <TabsContent value="llm" className="mt-6">
          <ErrorTabContent
            source="llm"
            errors={llmHook.errors}
            total={llmHook.total}
            page={llmHook.page}
            pageSize={llmHook.pageSize}
            loading={llmHook.loading}
            summary={llmHook.summary}
            distribution={llmHook.distribution}
            summaryLoading={llmHook.summaryLoading}
            distributionLoading={llmHook.distributionLoading}
            selectedErrorCode={selectedErrorCode}
            onSelectErrorCode={setSelectedErrorCode}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        {/* Functions Tab */}
        <TabsContent value="function" className="mt-6">
          <ErrorTabContent
            source="function"
            errors={functionHook.errors}
            total={functionHook.total}
            page={functionHook.page}
            pageSize={functionHook.pageSize}
            loading={functionHook.loading}
            summary={functionHook.summary}
            distribution={functionHook.distribution}
            summaryLoading={functionHook.summaryLoading}
            distributionLoading={functionHook.distributionLoading}
            selectedErrorCode={selectedErrorCode}
            onSelectErrorCode={setSelectedErrorCode}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        {/* Extraction Tab */}
        <TabsContent value="extraction" className="mt-6">
          <ErrorTabContent
            source="extraction"
            errors={extractionHook.errors}
            total={extractionHook.total}
            page={extractionHook.page}
            pageSize={extractionHook.pageSize}
            loading={extractionHook.loading}
            summary={extractionHook.summary}
            distribution={extractionHook.distribution}
            summaryLoading={extractionHook.summaryLoading}
            distributionLoading={extractionHook.distributionLoading}
            selectedErrorCode={selectedErrorCode}
            onSelectErrorCode={setSelectedErrorCode}
            onPageChange={handlePageChange}
          />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
